import { Dispatch, useEffect, useMemo, useRef, FC } from 'react';
import { useRouter } from 'next/router';
import { useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { stringify } from 'qs';
import noop from 'lodash/noop';
import {
  PaymentRequest,
  PaymentRequestOptions,
  PaymentRequestShippingAddress,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOption,
  PaymentRequestShippingOptionEvent,
  PaymentRequestTokenEvent,
  PaymentRequestUpdateDetails,
} from '@stripe/stripe-js';
import Bugsnag from '@bugsnag/js';

import { actions } from 'checkout/state/checkoutActions';
import { IAction, IExpressPayEnabledAction, IErrorAction } from 'checkout/types/ActionPayload';
import { IAddress } from 'checkout/types/Address';
import { IOrder, IPartialOrder } from 'checkout/types/Order';
import { requestService } from 'shared/utils/services/requestService';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useLocation } from 'shared/hooks/useLocation';
import { useOrder } from 'checkout/hooks/useOrder';
import { usePayments } from 'checkout/hooks/usePayments';
import { useShippingAddresses } from 'checkout/hooks/useShippingAddresses';
import { useCheckoutGlobalPricing } from 'checkout/hooks/useCheckoutGlobalPricing';
import { fireTiktokCheckoutPlaceOrder } from 'tracking/utils/tiktokEvents';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { logEcomPurchase } from 'tracking/utils/eCommerceEvents';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { GiftCardEventTypes } from 'gift-cards/enums/constants';
import { PaymentType } from 'shared/enums/PaymentType';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { login } from 'store/slice/authSlice';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'checkout.hooks.useExpressPay';

type PaymentRequestUpdateEvents =
  | PaymentRequestShippingAddressEvent
  | PaymentRequestShippingOptionEvent;

interface IUseExpressPayProps {
  addresses: { [id: number]: IAddress };
  dispatch: Dispatch<IAction | IErrorAction | IExpressPayEnabledAction>;
  order?: IPartialOrder;
}

interface IExpressPayOrderInput {
  billingAddress: IAddress;
  billingInfo: {
    name?: string;
    orderNumber?: number;
    stripeToken: string;
  };
  email?: string;
  finalPriceCents: number;
  isInstantShip?: boolean;
  orderNumber?: number;
  product?: {
    id?: number;
    productTemplate?: { id?: number };
    size?: { value?: number };
  };
  shippingAddress: IAddress;
  shippingServiceLevelId: string;
}

interface IGetStripeBuyDataProps {
  _order?: IPartialOrder;
  event: PaymentRequestTokenEvent;
}

interface IGetStripePaymentRequest {
  _order?: IPartialOrder;
  currencyCode?: string;
}

interface IUseExpressPay {
  buyWithExpressPay: (input: IExpressPayOrderInput) => void;
  createStripeSessionAndCheckCapability: () => void;
  ExpressPayButton: FC<{ onTrackerClick?: () => void }>;
  getStripeBuyData: ({ _order, event }: IGetStripeBuyDataProps) => IExpressPayOrderInput;
  getStripeDisplayOptions: (_order?: IPartialOrder) => PaymentRequestUpdateDetails;
  getStripePaymentRequestOptions: ({
    _order,
    currencyCode,
  }: IGetStripePaymentRequest) => PaymentRequestOptions;
  getStripeShippingOptions: (_order: IPartialOrder) => void;
  handlePaymentToken: (event: PaymentRequestTokenEvent) => void;
  handleShippingUpdate: (event: PaymentRequestUpdateEvents) => void;
}

export const useExpressPay = ({
  addresses,
  dispatch,
  order,
}: IUseExpressPayProps): IUseExpressPay => {
  const router = useRouter();
  const stripe = useStripe();
  const { selectedCountryCode } = useLocation();
  const { selectedCurrencyCode } = useCurrency();
  const { user, isAuthenticated } = useStoreSelector((state) => state.auth);
  const storeDispatch = useStoreDispatch();
  const { changeShippingServiceLevel, createShippingAddress } = useShippingAddresses({
    addresses,
    dispatch,
    order,
  });
  const { connectBillingInfoToOrder, createBillingAddress, createStripeBillingInfo } = usePayments({
    dispatch,
    order,
  });
  const { purchaseOrder } = useOrder({ dispatch, order });
  const { formatStripeCurrencyAmount, getStripeCheckoutGlobalPricingBreakdown } =
    useCheckoutGlobalPricing();

  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );

  const isPurchasingGiftCard = !!order?.product?.giftCardId && isCheckoutPurchaseGiftCardsEnabled;

  let giftCardType = GiftCardEventTypes.NULL;
  if (order?.product?.giftCardId) {
    giftCardType = GiftCardEventTypes.PURCHASED;
  }
  if (order?.giftCards?.length > 0) {
    giftCardType = GiftCardEventTypes.APPLIED;
  }

  /**
   * ExpressPayButton will not re-render while the sheet is popped so we need a
   * ref for shippingAddress / shippingOption
   */
  const paymentRequest = useRef<PaymentRequest>();
  const shippingAddress = useRef<PaymentRequestShippingAddress>();
  const shippingOption = useRef<PaymentRequestShippingOption>();
  const updatedOrder = useRef<IPartialOrder>();

  const buyWithExpressPay = async (input: IExpressPayOrderInput) => {
    const {
      billingAddress,
      billingInfo,
      finalPriceCents,
      shippingAddress: _shippingAddress,
      shippingServiceLevelId,
      email,
      isInstantShip,
      product,
    } = input;

    let orderNumber = input.orderNumber || undefined;

    if (!orderNumber) {
      try {
        const guestUser = await requestService({
          url: '/web-api/v1/create_guest',
          method: 'post',
          data: { guestEmail: email },
          withCredentials: true,
        });

        storeDispatch(login({ user: guestUser }));

        const orderEndpoint = product?.id
          ? '/web-api/v1/orders'
          : '/web-api/v1/orders/create_for_product_template';

        const orderInfo = {
          boxCondition: router?.query?.boxCondition,
          expectedPriceCents: Number(finalPriceCents) || null,
          instantShip: isInstantShip,
          productId: Number(product?.id) || null,
          productTemplateId: Number(product?.productTemplate?.id) || null,
          size: product?.size?.value,
        };

        for (const key of Object.keys(orderInfo)) {
          if (!orderInfo[key]) {
            delete orderInfo[key];
          }
        }

        const guestOrder = await requestService({
          url: orderEndpoint,
          method: 'post',
          data: { order: orderInfo },
          withCredentials: true,
        });

        orderNumber = guestOrder.number;
      } catch (error) {
        if (!error?.message) {
          error.message = 'Unable to create guest user or guest order.';
        }

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        throw error;
      }
    }

    if (!orderNumber) {
      const error = new Error(
        'Unable to create order for express pay, please try another payment. (Missing order number)',
      );

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });

      throw error;
    }

    await createShippingAddress(_shippingAddress, null, orderNumber);

    await changeShippingServiceLevel(shippingServiceLevelId, orderNumber);

    const _billingAddress = await createBillingAddress(billingAddress);

    if (!_billingAddress || !_billingAddress.id) {
      const error = new Error('Unable to create billing address.');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      throw error;
    }

    const _billingInfo = await createStripeBillingInfo({
      billingAddressId: _billingAddress?.id,
      name: billingInfo?.name,
      stripeToken: billingInfo?.stripeToken,
      paymentType: PaymentType.ApplePay,
    });

    await connectBillingInfoToOrder(_billingInfo.id, orderNumber);

    await purchaseOrder({
      finalPriceCents,
      expressPayOrderNumber: orderNumber,
      paymentType: PaymentType.ApplePay,
    });

    logEcomPurchase(
      {
        name: updatedOrder.current?.product?.productTemplate?.name,
        sku: updatedOrder.current?.product?.productTemplate?.sku,
        priceCents: updatedOrder.current?.product?.localizedPriceCents?.amountUsdCents,
        brandName: updatedOrder.current?.product?.productTemplate?.brandName,
        condition: updatedOrder.current?.product?.shoeCondition,
      },
      {
        number: updatedOrder.current?.number,
        priceCents: updatedOrder.current?.priceCents,
      },
    );

    sendTrackingEvent('CART_PURCHASE', {
      content_type: 'product_group',
      currency_code: selectedCurrencyCode,
      goat_storage: false,
      instant_ship: updatedOrder.current?.isInstantShip,
      is_apparel_product: updatedOrder.current?.product?.productTemplate?.isFashionProduct,
      order_id: updatedOrder.current?.id,
      order_number: updatedOrder.current?.number,
      price: updatedOrder.current?.localizedPriceCents?.amount,
      product_template_slug: updatedOrder.current?.product?.productTemplate?.slug,
      shipping_method: updatedOrder.current?.shippingServiceLevel,
      shipping_price: updatedOrder.current?.shippingCents,
      size: updatedOrder.current?.product?.size,
      subtotal_price_dollars: (
        (updatedOrder.current?.localizedPriceCents?.amountUsdCents || 0) / 100
      ).toFixed(2),
      subtotal_price: updatedOrder.current?.localizedPriceCents?.amount,
      gift_card_type: giftCardType,
    });

    if (isTiktokEnabled) {
      fireTiktokCheckoutPlaceOrder(
        {
          contentCategory: updatedOrder.current?.product?.productTemplate?.brandName,
          contentId: updatedOrder.current?.product?.productTemplate?.sku,
          currency: selectedCurrencyCode,
          price: updatedOrder.current?.localizedPriceCents?.amount,
          quantity: 1,
          value: updatedOrder.current?.localizedFinalPriceCents?.amount,
        },
        user?.email,
      );
    }
  };

  const getStripeBuyData = ({ _order, event }: IGetStripeBuyDataProps): IExpressPayOrderInput => {
    const {
      payerEmail,
      payerName,
      payerPhone,
      shippingAddress: _shippingAddress,
      shippingOption: _shippingOption,
      token,
    } = event;
    const orderNumber = _order?.number;

    const orderIdentifier: number | string = orderNumber || 'guest express checkout';

    if (
      !Number.isFinite(_order?.localizedFinalPriceCents?.amount) ||
      !Number.isFinite(_order?.localizedFinalPriceCents?.amountUsdCents)
    ) {
      const error = new Error(
        'There was an issue checking out with express pay, please choose a different payment method.',
      );

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: orderIdentifier });
      });

      throw error;
    }

    if (!shippingAddress) {
      const error = new Error(
        `No shipping address provided, cannot create stripe payment for ${orderIdentifier}`,
      );

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: orderIdentifier });
      });

      throw error;
    }

    if (!_shippingOption?.id) {
      const error = new Error(
        `No shipping option provided, cannot create stripe payment for ${orderIdentifier}`,
      );

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: orderIdentifier });
      });

      throw error;
    }

    if (!token?.id) {
      const error = new Error(
        `No stripe token, cannot create stripe payment for ${orderIdentifier}`,
      );

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: orderIdentifier });
      });

      throw error;
    }

    if (window.sessionStorage.getItem('apple-pay-token') === token.id) {
      const error = new Error('Apple Pay token already used.');

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: orderIdentifier });
      });

      throw error;
    }

    window.sessionStorage.setItem('apple-pay-token', token.id);

    const billingAddress: IAddress = {
      name: payerName || '',
      address1: token.card?.address_line1 || '',
      address2: token.card?.address_line2 || '',
      city: token.card?.address_city || '',
      countryCode: token.card?.address_country || '',
      postalCode: token.card?.address_zip || '',
      phone: payerPhone || '',
      state: token.card?.address_state || '',
    };

    const address: IAddress = {
      name: payerName || _shippingAddress?.recipient || '',
      address1: _shippingAddress?.addressLine?.shift() || token.card?.address_line1 || '',
      address2: _shippingAddress?.addressLine?.shift() || token.card?.address_line2 || '',
      city: _shippingAddress?.city || token.card?.address_city || '',
      countryCode: _shippingAddress?.country || token.card?.address_country || '',
      postalCode: _shippingAddress?.postalCode || token.card?.address_zip || '',
      phone: payerPhone || _shippingAddress?.phone || '',
      state: _shippingAddress?.region || token.card?.address_state || '',
    };

    const product = { ..._order?.product };

    return {
      billingAddress,
      billingInfo: {
        name: payerName,
        orderNumber,
        stripeToken: String(token.id),
      },
      email: payerEmail,
      /**
       * NOTE: regardless of currency, sending USD as this is what the backend
       * is expecting
       */
      finalPriceCents: _order?.finalPriceCents,
      isInstantShip: _order?.isInstantShip,
      orderNumber,
      product: {
        id: Number(product?.id),
        productTemplate: { id: Number(product?.productTemplate?.id) },
        size: { value: Number(product?.size) },
      },
      shippingAddress: address,
      shippingServiceLevelId: _shippingOption?.id,
    };
  };

  const getStripeShippingOptions = ({
    shippingServiceLevelOptions = [],
    shippingServiceLevel,
  }: IPartialOrder) => {
    return shippingServiceLevelOptions
      .filter((option) => option.available)
      .map((option) => ({
        id: option.id,
        amount: option?.localizedPriceCents?.amount,
        detail: option.description,
        label: option.name,
      }))
      .sort((optionA, optionB) => {
        /**
         * Apple pay displays the first option as selected so we sort our
         * selected option to first
         */
        if (optionA.id === shippingServiceLevel) return -1;
        if (optionB.id === shippingServiceLevel) return 1;
        return 0;
      });
  };

  const getStripeDisplayOptions = (_order?: IPartialOrder): PaymentRequestUpdateDetails => {
    const finalPrice = Number(_order?.localizedFinalPriceCents?.amount);

    if (!_order || Number.isNaN(finalPrice)) {
      const error = new Error('Apple Pay token already used.');

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: null });
      });

      throw error;
    }

    return {
      displayItems: getStripeCheckoutGlobalPricingBreakdown(_order),
      shippingOptions: getStripeShippingOptions(_order),
      total: { label: t(`${TP}.goat`, 'GOAT'), amount: formatStripeCurrencyAmount(finalPrice) },
    };
  };

  const getStripePaymentRequestOptions = ({
    _order,
    currencyCode = 'usd',
  }: IGetStripePaymentRequest): PaymentRequestOptions => {
    const finalPrice = Number(_order?.localizedFinalPriceCents?.amount);

    /**
     * NOTE: for guest checkout stripe is initialized at the guest checkout landing page
     * which at that time no final price exists, therefore we will ignore this
     * error and only send to bugsnag if it is an authenticated user with no final
     * price
     */
    if (Number.isNaN(finalPrice) && isAuthenticated) {
      const error = new Error('Invalid, no final price');

      /**
       * NOTE: only when there is an order number send to bugsnag so that we do not
       * get alerted for guest checkout flows that do not have an order yet
       */
      if (Boolean(_order?.number) || Boolean(router?.query?.orderNumber)) {
        Bugsnag.notify(error, (_event) => {
          _event.severity = 'error';
          _event.context = 'checkout';
          // NOTE: temporarily pass in order object to metadata to debug
          _event.addMetadata('checkout', {
            _order,
            orderNumber: order?.number || router?.query?.orderNumber,
          });
        });
      }

      throw error;
    }

    if (!_order) {
      const error = new Error('getStripePaymentRequestOptions must have an order object');

      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: _order?.number });
      });

      throw error;
    }

    let country = addresses[_order?.addressId]?.countryCode || selectedCountryCode;

    /**
     * NOTE: stripe's country code for united kingdom is 'GB'. this could be because
     * we are on stripes 2016 version however we must change from our internal
     * countryCode of UK to stripes GB in order for our UK users to be able to
     * use express pay
     */
    if (country === 'UK') {
      country = 'GB';
    }

    return {
      country,
      currency: currencyCode.toLowerCase(),
      displayItems: getStripeCheckoutGlobalPricingBreakdown(_order),
      requestPayerEmail: true,
      requestPayerName: true,
      requestPayerPhone: true,
      requestShipping: !isPurchasingGiftCard,
      total: {
        label: t(`${TP}.goat`, 'GOAT'),
        amount: formatStripeCurrencyAmount(finalPrice),
      },
    };
  };

  /**
   * Payment tokens are generated after a user authorizes an apple pay transaction.
   * Sneakers uses this token to complete payment via stripe
   */
  const handlePaymentToken = async (event: PaymentRequestTokenEvent) => {
    dispatch({ type: actions.expressCheckoutRequest });

    try {
      /**
       * NOTE: for guest express checkout updated order is not complete therefore must
       * populate missing information from query params passed from PDP
       */
      if (isGuestCheckoutEnabled && !isAuthenticated) {
        updatedOrder.current = {
          ...updatedOrder.current,
          finalPriceCents: updatedOrder.current?.localizedFinalPriceCents?.amountUsdCents,
          isInstantShip: router?.query?.instantShip === 'true',
          product: {
            id: Number(router?.query?.productId) || null,
            productTemplate: {
              id: Number(router?.query?.productTemplateId) || null,
            },
            size: Number(router?.query?.size) || null,
          },
        };
      }

      const expressPayOrder = getStripeBuyData({ _order: updatedOrder.current, event });

      await buyWithExpressPay(expressPayOrder);

      event.complete('success');

      void router.push(
        getRedirectionPath(`/checkout/${updatedOrder.current?.number}/success`, router?.locale),
      );
    } catch (error) {
      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: updatedOrder?.current?.number });
      });

      if (error?.response?.status === 401) {
        dispatch({
          type: actions.checkoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });
      } else {
        dispatch({
          type: actions.expressCheckoutFailure,
          payload: error.message,
        });
      }

      event.complete('fail');
    }
  };

  /**
   * We do not send apple any address info, all address info comes from what the
   * user has saved for apple pay. Initially our order pricing is based off our
   * stored address. Upon opening of apple pay window, shippingAddressChange
   * event is sent and we can update order based on apple address info (which is
   * only a partial address so we update on an order estimate, but do NOT update
   * the actual order until checkout).
   */
  const handleShippingUpdate = async (event: PaymentRequestUpdateEvents) => {
    if ('shippingAddress' in event) {
      shippingAddress.current = event.shippingAddress;
    }

    if ('shippingOption' in event) {
      shippingOption.current = event.shippingOption;
    }

    const addressDetail = {
      city: shippingAddress.current?.city,
      countryCode: shippingAddress.current?.country,
      postalCode: shippingAddress.current?.postalCode,
      state: shippingAddress.current?.region,
    };

    let orderDetail = {
      instantShip: updatedOrder.current?.isInstantShip,
      productId: Number(updatedOrder.current?.product?.id) || null,
      productTemplateId: Number(updatedOrder.current?.product?.productTemplate?.id) || null,
      shippingServiceLevel:
        shippingOption.current?.id || updatedOrder.current?.shippingServiceLevel || null,
      size: Number(updatedOrder.current?.product?.size) || null,
    };

    /**
     * NOTE: for guest checkout express the initial order details is incomplete therefore must
     * populate missing information from query params passed from PDP
     */
    if (isGuestCheckoutEnabled && !isAuthenticated) {
      orderDetail = {
        instantShip: router?.query?.instantShip === 'true',
        productId: Number(router?.query?.productId) || null,
        productTemplateId: Number(router?.query?.productTemplateId) || null,
        shippingServiceLevel:
          shippingOption.current?.id || updatedOrder.current?.shippingServiceLevel || null,
        size: Number(router?.query?.size) || null,
      };
    }

    try {
      const param = {
        'address[city]': addressDetail.city,
        'address[countryCode]': addressDetail.countryCode,
        'address[postalCode]': addressDetail.postalCode,
        'address[state]': addressDetail.state,
        'order[instantShip]': orderDetail.instantShip,
        'order[productId]': orderDetail.productId,
        'order[productTemplateId]': orderDetail.productTemplateId,
        'order[shippingServiceLevel]': orderDetail.shippingServiceLevel,
        'order[size]': orderDetail.size,
      };

      const queryParams = stringify(param, { encodeValuesOnly: true });

      const _order: IOrder = await requestService({
        url: isAuthenticated
          ? `/web-api/v1/orders/${updatedOrder.current?.number}/price_estimates?${queryParams}`
          : `/web-api/v1/orders/guest_price_estimates?${queryParams}`,
        method: 'get',
      });

      /**
       * CHECKOUT TODO: does not include promotions
       * possible can just pass in the order object but for now will use the
       * same order partial as FC
       */
      const partialOrder = {
        finalPriceCents: _order?.finalPriceCents,
        localizedCreditCents: _order?.localizedCreditCents,
        localizedFinalPriceCents: _order?.localizedFinalPriceCents,
        localizedPriceCents: _order?.localizedPriceCents,
        localizedShippingCents: _order?.localizedShippingCents,
        localizedTaxCents: _order?.localizedTaxCents,
        shippingServiceLevel: _order?.shippingServiceLevel,
        shippingServiceLevelOptions: _order?.shippingServiceLevelOptions,
      };

      updatedOrder.current = { ...updatedOrder.current, ...partialOrder };

      const displayOptions = getStripeDisplayOptions(updatedOrder.current);

      displayOptions.status = 'success';

      event.updateWith(displayOptions);
    } catch (error) {
      Bugsnag.notify(error, (_event) => {
        _event.severity = 'error';
        _event.context = 'checkout';
        _event.addMetadata('checkout', { orderNumber: updatedOrder?.current?.number });
      });

      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });
        return;
      }

      dispatch({ type: actions.expressCheckoutFailure, payload: error.message });

      event.updateWith({ status: 'fail' });
    }
  };

  const createStripeSessionAndCheckCapability = async () => {
    try {
      const paymentRequestOptions = getStripePaymentRequestOptions({
        _order: order,
        currencyCode: selectedCurrencyCode,
      });

      paymentRequest.current = stripe?.paymentRequest(paymentRequestOptions);
      paymentRequest.current?.on('shippingaddresschange', handleShippingUpdate);
      paymentRequest.current?.on('shippingoptionchange', handleShippingUpdate);
      paymentRequest.current?.on('token', handlePaymentToken);

      const canMakePayment = await paymentRequest.current?.canMakePayment();

      dispatch({ type: 'applePayAvailable', payload: canMakePayment?.applePay });
    } catch (error) {
      /**
       * NOTE: only when there is an order number send to bugsnag so that we do not
       * get alerted for guest checkout flows that do not have an order yet. Also
       * for guest checkout stripe is initialized at the guest checkout landing page
       * which at that time no final price exists, therefore we will ignore this
       * error and only send to bugsnag if it is an authenticated user with no final
       * price
       */
      if (
        Boolean(order?.number) ||
        Boolean(router?.query?.orderNumber) ||
        (isAuthenticated && Boolean(Number(order?.localizedFinalPriceCents?.amount)))
      ) {
        Bugsnag.notify(error, (_event) => {
          _event.severity = 'error';
          _event.context = 'checkout';
          // NOTE: temporarily pass in order object to metadata to debug
          _event.addMetadata('checkout', {
            order,
            orderNumber: order?.number || router?.query?.orderNumber,
          });
        });
      }
    }
  };

  useEffect(() => {
    if (stripe && !!order) {
      updatedOrder.current = order;

      void createStripeSessionAndCheckCapability();
    }
  }, [stripe, order]);

  /**
   * Update the displayItems object if promotion id changes to capture new
   * promotion price
   */
  useEffect(() => {
    if (stripe) {
      updatedOrder.current = order;
      paymentRequest.current?.update(getStripeDisplayOptions(order));
    }
  }, [stripe, !!paymentRequest.current]);

  // useMemo to stop Apple Pay button from re-rendering
  const ExpressPayButton = useMemo(() => {
    if (paymentRequest.current) {
      return ({ onTrackerClick = noop }) => (
        <PaymentRequestButtonElement
          options={{
            paymentRequest: paymentRequest.current,
            style: {
              paymentRequestButton: {
                theme: 'dark',
                height: '32px',
              },
            },
          }}
          onClick={onTrackerClick}
        />
      );
    }

    return () => null;
  }, [!!paymentRequest.current]);

  return {
    buyWithExpressPay,
    createStripeSessionAndCheckCapability,
    ExpressPayButton,
    getStripeBuyData,
    getStripeDisplayOptions,
    getStripePaymentRequestOptions,
    getStripeShippingOptions,
    handlePaymentToken,
    handleShippingUpdate,
  };
};
